import type { NavigateOptions, RouteObject, createBrowserRouter, createMemoryRouter } from "react-router";
import type { EventCallback, EventType } from "../../util/event-manager";
import type { Services } from "../services";
import { EventManager } from "../../util/event-manager";


export type NavigateEvent = {
	state: RouterState;
};
export type SearchParamChangeEvent = {
	params: URLSearchParams;
};
export type HashChangeEvent = {
	hash: string | null;
};

export type NavigationEvents = {
	"navigate": NavigateEvent;
	"searchParamsChange": SearchParamChangeEvent;
	"hashChange": HashChangeEvent;
};

/**
 * Function invoked to construct the Router itself.
 *
 * @see {@link createBrowserRouter}
 * @see {@link createMemoryRouter}
 */
export type RouterFactory = typeof createBrowserRouter | typeof createMemoryRouter;

/**
 * Function invoked to construct the application's routes.
 */
export type RoutesFactory = (services: Omit<Services, "navigation">) => RouteObject[];

type Router = ReturnType<typeof createBrowserRouter>;
type RouterState = Router["state"];

export type NavigationServiceConfig = {
	routerFactory: RouterFactory;
	routesFactory: RoutesFactory;
};
export class NavigationService {
	readonly #config: Readonly<NavigationServiceConfig>;

	#router: Router | null = null;
	#state: RouterState | null = null;

	readonly events = new EventManager<NavigationEvents>();

	constructor(config: Readonly<NavigationServiceConfig>) {
		this.#config = config;
	}

	/**
	 * Called by Services.init().
	 */
	private initialise(services: Services): void {
		this.#router = this.#config.routerFactory(this.#config.routesFactory(services));
		this.#state = this.#router.state;
		this.#router.subscribe(this.#handleRouterChange);
	}

	readonly #handleRouterChange = (state: RouterState) => {
		if(state.location.pathname === this.state.location.pathname) {
			if(state.location.search !== this.state.location.search) {

				this.events.notify("searchParamsChange", {
					params: new URLSearchParams(state.location.search),
				});
			}
			if(state.location.hash !== this.#state?.location.hash) {
				this.events.notify("hashChange", {
					hash: state.location.hash.replace("#", "") || null,
				});
			}
		}

		this.#state = state;

		this.events.notify("navigate", { state });
	};

	/**
	 * Navigate to a new route.
	 */
	navigate(to: string, options?: NavigateOptions): Promise<void> {
		return this.router.navigate(to, options);
	}

	async navigateToHash(hash: string | null, replace = false): Promise<void> {
		await this.router.navigate({
			...this.state.location,
			hash: hash ?? "",
		}, { replace });
	}

	#makeSearchParams(params: Record<string, string | undefined | null> | undefined | null): string {
		if(!params) {
			return "";
		}

		const cleanParams = Object.fromEntries(
			Object.entries(params).filter(([ _, value ]) => value !== undefined && value !== null)
		) as Record<string, string>;

		if(!Object.keys(cleanParams).length) {
			return "";
		}

		return "?" + new URLSearchParams(cleanParams).toString();
	}

	/**
	 * Set all URL search params.
	 */
	async setSearchParams(params: Readonly<Record<string, string | undefined | null>> | undefined | null, options?: NavigateOptions): Promise<void> {
		await this.router.navigate({
			...this.state.location,
			search: this.#makeSearchParams(params),
		}, options);
	}

	/**
	 * Merge `params` with existing URL search params.
	 */
	async patchSearchParams(params: Readonly<Record<string, string | undefined | null>> | undefined | null, options?: NavigateOptions): Promise<void> {
		const previous = Object.fromEntries(this.getSearchParams().entries());
		const next = {
			...previous,
			...params,
		};

		await this.setSearchParams(next, options);
	}

	/**
	 * Set a single URL search param.
	 */
	async setSearchParam(key: string, value: string | undefined | null, options?: NavigateOptions): Promise<void> {
		if(this.getSearchParam(key) === value) {
			return;
		}

		await this.patchSearchParams({ [key]: value }, options);
	}

	getSearchParams(): URLSearchParams {
		return new URLSearchParams(this.router.state.location.search);
	}

	getSearchParam(key: string): string | null {
		return this.getSearchParams().get(key);
	}

	getRouteParams(): Readonly<Record<string, string | undefined>> {
		const match = this.#state?.matches.at(-1);
		return match?.params ?? {};
	}

	getPath(): string {
		const match = this.#state?.matches.at(-1);
		return match?.pathname ?? "/";
	}

	get router(): Router {
		if(!this.#router) {
			throw new Error("NavigationService was not initialised.");
		}

		return this.#router;
	}

	get state(): RouterState {
		if(!this.#state) {
			throw new Error("NavigationService was not initialised.");
		}

		return this.#state;
	}

	/**
	 * The current location's `#hash` fragment, **without** the leading `#`,
	 * or `null` if there is no hash fragment.
	 */
	get hash(): string | null {
		return this.state.location.hash.replace("#", "") || null;
	}

	on<E extends EventType<NavigationEvents>>(ev: E, callback: EventCallback<NavigationEvents, E>): this {
		this.events.on(ev, callback);
		return this;
	}

	off<E extends EventType<NavigationEvents>>(ev: E, callback: EventCallback<NavigationEvents, E>): this {
		this.events.off(ev, callback);
		return this;
	}
}
